import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Visibility,
  Settings,
  Security,
  Business,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import ValidadoLogo from '../components/ValidadoLogo';
import usePageTitle from '../hooks/usePageTitle';

interface ClientConfig {
  companyId: string;
  companyName: string;
  email: string;
  configuration: {
    portalType: string;
    branding: {
      primaryColor: string;
      secondaryColor: string;
      logoUrl: string | null;
      companyName: string;
      customDomain: string | null;
    };
    features: {
      csvUpload: boolean;
      bulkProcessing: boolean;
      statusTracking: boolean;
      reportGeneration: boolean;
      apiAccess: boolean;
      customFields: boolean;
    };
    dashboardLayout: string;
    allowedEmailDomains: string[];
  };
  assignmentManager: any;
  isActive: boolean;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientConfigAdminPage: React.FC = () => {
  usePageTitle('Client Configuration Admin');
  
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState<ClientConfig[]>([]);
  const [, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientConfig | null>(null);
  const [adminEmail] = useState('tapia@over.do'); // This would come from auth context
  
  // Form state for new/edit client
  const [formData, setFormData] = useState({
    companyName: '',
    portalType: 'standard',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    logoUrl: '',
    customDomain: '',
    emailDomains: '',
    features: {
      csvUpload: true,
      bulkProcessing: false,
      statusTracking: true,
      reportGeneration: false,
      apiAccess: false,
      customFields: false
    },
    dashboardLayout: 'standard'
  });

  useEffect(() => {
    loadClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/client-config/all?adminEmail=${adminEmail}`);
      if (response.data.success) {
        setClients(response.data.data);
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load clients' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      const emailDomains = formData.emailDomains.split(',').map(d => d.trim()).filter(d => d);
      
      const response = await axios.post('/api/client-config/create-default', {
        companyName: formData.companyName,
        portalType: formData.portalType,
        adminEmail: adminEmail,
        emailDomains: emailDomains,
        customBranding: {
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          logoUrl: formData.logoUrl || null,
          companyName: formData.companyName,
          customDomain: formData.customDomain || null
        }
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Client configuration created successfully!' });
        setDialogOpen(false);
        resetForm();
        loadClients();
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create client' });
    }
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;
    
    try {
      const emailDomains = formData.emailDomains.split(',').map(d => d.trim()).filter(d => d);
      
      const configuration = {
        portalType: formData.portalType,
        branding: {
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          logoUrl: formData.logoUrl || null,
          companyName: formData.companyName,
          customDomain: formData.customDomain || null
        },
        features: formData.features,
        dashboardLayout: formData.dashboardLayout,
        allowedEmailDomains: emailDomains
      };
      
      const response = await axios.put('/api/client-config/update', {
        companyName: editingClient.companyName,
        configuration: configuration,
        adminEmail: adminEmail
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Client configuration updated successfully!' });
        setDialogOpen(false);
        setEditingClient(null);
        resetForm();
        loadClients();
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update client' });
    }
  };

  const handleEditClient = (client: ClientConfig) => {
    setEditingClient(client);
    setFormData({
      companyName: client.companyName,
      portalType: client.configuration.portalType,
      primaryColor: client.configuration.branding.primaryColor,
      secondaryColor: client.configuration.branding.secondaryColor,
      logoUrl: client.configuration.branding.logoUrl || '',
      customDomain: client.configuration.branding.customDomain || '',
      emailDomains: client.configuration.allowedEmailDomains.join(', '),
      features: client.configuration.features,
      dashboardLayout: client.configuration.dashboardLayout
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      portalType: 'standard',
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      logoUrl: '',
      customDomain: '',
      emailDomains: '',
      features: {
        csvUpload: true,
        bulkProcessing: false,
        statusTracking: true,
        reportGeneration: false,
        apiAccess: false,
        customFields: false
      },
      dashboardLayout: 'standard'
    });
  };

  const getPortalTypeColor = (type: string) => {
    switch (type) {
      case 'didi': return 'warning';
      case 'enterprise': return 'success';
      case 'custom': return 'secondary';
      default: return 'primary';
    }
  };

  const getFeatureCount = (features: any) => {
    return Object.values(features).filter(Boolean).length;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: '#1976d2', color: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Box sx={{ mr: 2 }}><ValidadoLogo /></Box>
            <Box>
              <Typography variant="h4" component="h1">
                Client Configuration Admin
              </Typography>
              <Typography variant="subtitle1">
                Manage multi-client SaaS configurations
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white', mr: 2 }}
              onClick={loadClients}
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: 'white', color: '#1976d2' }}
              onClick={() => {
                setEditingClient(null);
                resetForm();
                setDialogOpen(true);
              }}
              startIcon={<Add />}
            >
              Add Client
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Message Alert */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clients
              </Typography>
              <Typography variant="h4">
                {clients.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Clients
              </Typography>
              <Typography variant="h4" color="success.main">
                {clients.filter(c => c.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Enterprise Clients
              </Typography>
              <Typography variant="h4" color="primary.main">
                {clients.filter(c => c.configuration.portalType === 'enterprise').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Custom Portals
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {clients.filter(c => c.configuration.portalType === 'custom').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Client List" icon={<Business />} />
            <Tab label="Portal Types" icon={<Settings />} />
            <Tab label="Feature Matrix" icon={<Security />} />
          </Tabs>
        </Box>

        {/* Client List Tab */}
        <TabPanel value={activeTab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Portal Type</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Email Domains</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.companyId}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {client.companyName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {client.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.portalType.toUpperCase()}
                        color={getPortalTypeColor(client.configuration.portalType) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${getFeatureCount(client.configuration.features)}/6 Features`}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {client.configuration.allowedEmailDomains.join(', ') || 'None'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.isActive ? 'Active' : 'Inactive'}
                        color={client.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Configuration">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClient(client)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Portal Types Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {['standard', 'didi', 'enterprise', 'custom'].map((type) => {
              const count = clients.filter(c => c.configuration.portalType === type).length;
              return (
                <Card key={type} sx={{ flex: '1 1 250px', minWidth: 250 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {type.charAt(0).toUpperCase() + type.slice(1)} Portal
                    </Typography>
                    <Typography variant="h4" color={getPortalTypeColor(type) + '.main'}>
                      {count}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {type === 'standard' && 'Basic features for small businesses'}
                      {type === 'didi' && 'Specialized for transportation companies'}
                      {type === 'enterprise' && 'Full feature set for large organizations'}
                      {type === 'custom' && 'Fully customizable solutions'}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </TabPanel>

        {/* Feature Matrix Tab */}
        <TabPanel value={activeTab} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>CSV Upload</TableCell>
                  <TableCell>Bulk Processing</TableCell>
                  <TableCell>Status Tracking</TableCell>
                  <TableCell>Report Generation</TableCell>
                  <TableCell>API Access</TableCell>
                  <TableCell>Custom Fields</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.companyId}>
                    <TableCell>{client.companyName}</TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.csvUpload ? 'Yes' : 'No'}
                        color={client.configuration.features.csvUpload ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.bulkProcessing ? 'Yes' : 'No'}
                        color={client.configuration.features.bulkProcessing ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.statusTracking ? 'Yes' : 'No'}
                        color={client.configuration.features.statusTracking ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.reportGeneration ? 'Yes' : 'No'}
                        color={client.configuration.features.reportGeneration ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.apiAccess ? 'Yes' : 'No'}
                        color={client.configuration.features.apiAccess ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.configuration.features.customFields ? 'Yes' : 'No'}
                        color={client.configuration.features.customFields ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Create/Edit Client Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClient ? 'Edit Client Configuration' : 'Create New Client'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Company Name"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                fullWidth
                disabled={!!editingClient}
              />
              <FormControl fullWidth>
                <InputLabel>Portal Type</InputLabel>
                <Select
                  value={formData.portalType}
                  onChange={(e) => setFormData({ ...formData, portalType: e.target.value })}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="didi">DiDi</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Branding */}
            <Typography variant="h6" gutterBottom>
              Branding
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Primary Color"
                type="color"
                value={formData.primaryColor}
                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                sx={{ width: 120 }}
              />
              <TextField
                label="Secondary Color"
                type="color"
                value={formData.secondaryColor}
                onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                sx={{ width: 120 }}
              />
              <TextField
                label="Logo URL"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Custom Domain"
                value={formData.customDomain}
                onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
                fullWidth
                placeholder="verify.company.com"
              />
              <TextField
                label="Email Domains (comma-separated)"
                value={formData.emailDomains}
                onChange={(e) => setFormData({ ...formData, emailDomains: e.target.value })}
                fullWidth
                placeholder="company.com, company.org"
              />
            </Box>

            {/* Features */}
            <Typography variant="h6" gutterBottom>
              Features
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              {Object.entries(formData.features).map(([feature, enabled]) => (
                <FormControlLabel
                  key={feature}
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        features: {
                          ...formData.features,
                          [feature]: e.target.checked
                        }
                      })}
                    />
                  }
                  label={feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                />
              ))}
            </Box>

            {/* Dashboard Layout */}
            <Typography variant="h6" gutterBottom>
              Dashboard Layout
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Layout Type</InputLabel>
              <Select
                value={formData.dashboardLayout}
                onChange={(e) => setFormData({ ...formData, dashboardLayout: e.target.value })}
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="compact">Compact</MenuItem>
                <MenuItem value="detailed">Detailed</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingClient ? handleUpdateClient : handleCreateClient}
            variant="contained"
          >
            {editingClient ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientConfigAdminPage;